/**
 * `galleryinfo` JSONP parser + mapper.
 *
 * Hitomi serves gallery metadata as a single JavaScript file at
 * `https://ltn.hitomi.la/galleries/<id>.js`. The body is roughly:
 *
 *   ```
 *   var galleryinfo = { "id": "...", ... };
 *   ```
 *
 * Some older galleries omit the `var ` prefix. We strip whichever prefix is
 * present, then JSON-parse the rest. We never `eval` it.
 */

import { ParseError } from '../../lib/errors.ts';
import { proxiedImageUrl } from '../../lib/proxy.ts';
import type { ComicCard, ComicDetails, Tag } from '../types.ts';
import { computeImageUrl, deriveHashCode, BASE_DOMAIN } from './gg.ts';
import type { GgContext, HitomiImage, HitomiTag, RawGalleryInfo } from './types.ts';

/** Source-id constant — referenced in mapped `ComicCard`/`ComicDetails`. */
export const HITOMI_SOURCE_ID = 'hitomi' as const;

/** Prefixes hitomi has been known to use; we accept any of them. */
const JSONP_PREFIXES = ['var galleryinfo =', 'galleryinfo ='];

function galleryPageUrl(raw: RawGalleryInfo, id: string): string {
  if (raw.galleryurl) return new URL(raw.galleryurl, 'https://hitomi.la').toString();
  return `https://hitomi.la/galleries/${id}.html`;
}

/**
 * Strip the JSONP prefix and JSON-parse the body. Pure.
 *
 * @throws {ParseError} if no recognized prefix matches or the JSON is malformed.
 */
export function parseGalleryInfoJsonp(body: string): RawGalleryInfo {
  const trimmed = body.trimStart();
  let json: string | undefined;
  for (const prefix of JSONP_PREFIXES) {
    if (trimmed.startsWith(prefix)) {
      json = trimmed.slice(prefix.length).trimStart();
      break;
    }
  }
  if (json === undefined) {
    throw new ParseError('hitomi: galleryinfo JSONP prefix not recognized', {
      source: 'hitomi',
      snippet: trimmed.slice(0, 80),
    });
  }
  // Hitomi sometimes terminates the assignment with `;` — drop trailing
  // semicolons + whitespace before JSON.parse.
  json = json.replace(/;[\s\n\r]*$/, '');
  try {
    return JSON.parse(json) as RawGalleryInfo;
  } catch (err) {
    throw new ParseError('hitomi: galleryinfo body is not valid JSON', {
      source: 'hitomi',
      snippet: json.slice(0, 200),
      cause: err,
    });
  }
}

/**
 * Pull the structured list of tags out of a `RawGalleryInfo`. Combines the
 * dedicated-namespace fields (`artists`, `groups`, `parodys`, `characters`)
 * with the freeform `tags[]` (which is itself split into `male` / `female`
 * by per-entry flags).
 *
 * The output is a flat list of {@link HitomiTag}.
 */
export function extractTags(raw: RawGalleryInfo): HitomiTag[] {
  const out: HitomiTag[] = [];

  for (const a of raw.artists ?? []) {
    if (a?.artist) out.push({ namespace: 'artist', name: a.artist });
  }
  for (const g of raw.groups ?? []) {
    if (g?.group) out.push({ namespace: 'group', name: g.group });
  }
  // Hitomi's field is misspelled (`parodys`) but maps to "series" as a UI label.
  for (const p of raw.parodys ?? []) {
    if (p?.parody) out.push({ namespace: 'series', name: p.parody });
  }
  for (const c of raw.characters ?? []) {
    if (c?.character) out.push({ namespace: 'character', name: c.character });
  }
  for (const t of raw.tags ?? []) {
    if (!t?.tag) continue;
    const namespace: HitomiTag['namespace'] = isTruthy(t.male)
      ? 'male'
      : isTruthy(t.female)
        ? 'female'
        : 'tag';
    out.push({ namespace, name: t.tag });
  }
  if (raw.language) {
    out.push({ namespace: 'language', name: raw.language });
  }
  if (raw.type) {
    out.push({ namespace: 'type', name: raw.type });
  }
  return out;
}

function isTruthy(v: unknown): boolean {
  return v === 1 || v === '1' || v === true;
}

/**
 * Convert a hitomi tag list to the shared {@link Tag} shape used in
 * `ComicCard` / `ComicDetails`. The `id` is `<namespace>:<name>` (matching
 * the `[namespace:]name` blacklist format used elsewhere in the app).
 */
export function toSharedTags(tags: HitomiTag[]): Tag[] {
  return tags.map((t) => ({
    id: `${t.namespace}:${t.name}`,
    name: t.name,
    namespace: t.namespace,
  }));
}

/**
 * Convert raw `files[]` entries into our internal {@link HitomiImage} shape.
 */
export function toHitomiImages(raw: RawGalleryInfo): HitomiImage[] {
  return (raw.files ?? []).map((f) => ({
    hash: f.hash,
    name: f.name,
    width: f.width | 0,
    height: f.height | 0,
    hasAvif: isTruthy(f.hasavif),
    hasWebp: f.haswebp === undefined ? true : isTruthy(f.haswebp),
    hasJxl: isTruthy(f.hasjxl),
  }));
}

/**
 * Parse hitomi's loose "added date" string into a Unix-ms epoch. The format
 * has historically been `YYYY-MM-DD HH:MM:SS-TZ` (`-05` for Eastern, no
 * colon). We convert it to ISO-8601 before `Date.parse`.
 *
 * Returns `undefined` (not `NaN`) if the input is missing or unparseable.
 */
export function parseHitomiDate(input: string | null | undefined): number | undefined {
  if (!input) return undefined;
  // `2024-08-21 15:30:00-05` -> `2024-08-21T15:30:00-05:00`
  let iso = input.replace(' ', 'T');
  iso = iso.replace(/([+-]\d{2})$/, '$1:00');
  const ts = Date.parse(iso);
  return Number.isFinite(ts) ? ts : undefined;
}

/**
 * Pick a "cover" thumbnail URL for the gallery's representative image.
 *
 * Hitomi has a dedicated `tn` (thumbnail) subdomain at
 * `<format><smallbig|big|small>tn/<last1>/<last3..-1>/<hash>.<ext>`. For
 * cards we use the small variant; the reader uses full-resolution images via
 * {@link computeImageUrl}.
 *
 * Requires a fresh `GgContext` because the URL format pulls from `b: '...'`
 * for full images but the thumbnail subdomain doesn't actually need
 * gg.js-derived state. We still take the context for API consistency.
 */
/**
 * NOTE: returned URLs point at the worker proxy, NOT directly at the
 * `tn.<baseDomain>` CDN. Hitomi's image CDNs hot-link-protect against
 * any Referer that isn't `https://hitomi.la/`, and browsers can't spoof
 * Referer on cross-origin `<img>` loads. The worker handles the
 * substitution in `refererFor()`. The `baseDomain` opt is plumbed
 * through so tests can override the upstream string we wrap.
 */
export function coverUrlFor(
  image: Pick<HitomiImage, 'hash' | 'hasAvif'> | undefined,
  opts: { baseDomain?: string } = {},
): string {
  if (!image) return '';
  const baseDomain = opts.baseDomain ?? BASE_DOMAIN;
  // Thumbnails are NOT served via gg.js — they live on `tn.<baseDomain>`
  // under a per-format path; the URL is fully derivable from the hash alone.
  const ext = image.hasAvif ? 'avif' : 'webp';
  const last1 = image.hash.slice(-1);
  const last3to1 = image.hash.slice(-3, -1);
  const upstream = `https://tn.${baseDomain}/${ext}smalltn/${last1}/${last3to1}/${image.hash}.${ext}`;
  return proxiedImageUrl(upstream);
}

/**
 * Best-effort big-thumb URL — used by the gallery detail page for a hero
 * image. Same path scheme as the small thumb but under `*bigtn/`.
 * Same Referer constraint as {@link coverUrlFor}; routed via the worker.
 */
export function bigThumbUrlFor(
  image: Pick<HitomiImage, 'hash' | 'hasAvif'> | undefined,
  opts: { baseDomain?: string } = {},
): string {
  if (!image) return '';
  const baseDomain = opts.baseDomain ?? BASE_DOMAIN;
  const ext = image.hasAvif ? 'avif' : 'webp';
  const last1 = image.hash.slice(-1);
  const last3to1 = image.hash.slice(-3, -1);
  const upstream = `https://tn.${baseDomain}/${ext}bigtn/${last1}/${last3to1}/${image.hash}.${ext}`;
  return proxiedImageUrl(upstream);
}

/**
 * Map a raw `galleryinfo` payload to the canonical `ComicDetails` shape used
 * by the app. Pure: no IO, no caching, no gg.js fetching.
 */
export function mapToComicDetails(raw: RawGalleryInfo): ComicDetails {
  const id = String(raw.id ?? '');
  if (!id) {
    throw new ParseError('hitomi: galleryinfo missing id', { source: 'hitomi' });
  }

  const images = toHitomiImages(raw);
  const tags = toSharedTags(extractTags(raw));
  const cover = coverUrlFor(images[0]);

  const details: ComicDetails = {
    id,
    source: HITOMI_SOURCE_ID,
    title: raw.title?.trim() || `Gallery ${id}`,
    coverUrl: cover,
    pageCount: images.length,
    tags,
    sourceUrl: galleryPageUrl(raw, id),
  };
  if (raw.japanese_title) {
    details.altTitle = raw.japanese_title;
  }
  if (raw.language) {
    details.language = raw.language;
  }
  const uploadedAt = parseHitomiDate(raw.date);
  if (uploadedAt !== undefined) {
    details.uploadedAt = uploadedAt;
  }
  const artist = (raw.artists ?? [])[0]?.artist;
  if (artist) {
    details.uploader = artist;
  }
  return details;
}

/**
 * Map a raw payload to the lighter `ComicCard` shape used in lists/grids.
 * Mostly a subset of {@link mapToComicDetails} with optional fields omitted
 * when they aren't useful for cards.
 */
export function mapToComicCard(raw: RawGalleryInfo): ComicCard {
  const id = String(raw.id ?? '');
  if (!id) {
    throw new ParseError('hitomi: galleryinfo missing id', { source: 'hitomi' });
  }
  const images = toHitomiImages(raw);
  const tags = toSharedTags(extractTags(raw));
  const card: ComicCard = {
    id,
    source: HITOMI_SOURCE_ID,
    title: raw.title?.trim() || `Gallery ${id}`,
    coverUrl: coverUrlFor(images[0]),
    pageCount: images.length,
    tags,
  };
  if (raw.language) {
    card.language = raw.language;
  }
  const uploadedAt = parseHitomiDate(raw.date);
  if (uploadedAt !== undefined) {
    card.uploadedAt = uploadedAt;
  }
  return card;
}

/**
 * Convert a hitomi image to its full-size `<img src>` URL using a freshly
 * resolved `gg.js` context. Re-exported here so `api.ts` can build full
 * `PageRef`s without re-importing from `gg.ts` everywhere.
 */
export function imageUrlWithContext(
  image: HitomiImage,
  context: GgContext,
  opts: { extension?: 'avif' | 'webp'; baseDomain?: string } = {},
): string {
  return computeImageUrl(image, context, opts);
}

/** Re-export for tests that want to spot-check the hashCode helper. */
export const _hashCode = deriveHashCode;
